import {
  Box, Button, Checkbox, Chip, CircularProgress, FormControlLabel,
  Link, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useApiCallWithAuth } from '../../hooks/useApiCallWithAuth';
import { listSchedulesForBlog, listSocialAccounts, updateSchedule, upsertSchedulesForBlog } from '../../api/social';
import { SocialAccountItem, SocialPostScheduleItem } from '../../types/social';

interface Props {
  blogId: number;
}

interface DestinationRowState {
  checked: boolean;
  scheduledFor: string; // datetime-local input value
  messageOverride: string;
  existingGuid: string | null;
}

const STATUS_COLOR: Record<string, 'default' | 'success' | 'error' | 'info'> = {
  Pending: 'info',
  Publishing: 'info',
  Posted: 'success',
  Failed: 'error',
  Canceled: 'default',
};

function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function defaultLocalInput(): string {
  return toLocalInput(new Date(Date.now() + 60 * 60 * 1000).toISOString());
}

export function SocialSchedulePanel({ blogId }: Props) {
  const [accounts, setAccounts] = useState<SocialAccountItem[]>([]);
  const [schedules, setSchedules] = useState<SocialPostScheduleItem[]>([]);
  const [rows, setRows] = useState<Record<string, DestinationRowState>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { request: listAccountsRequest } = useApiCallWithAuth(listSocialAccounts);
  const { request: listSchedulesRequest } = useApiCallWithAuth(listSchedulesForBlog);
  const { request: upsertRequest } = useApiCallWithAuth(upsertSchedulesForBlog);
  const { request: updateRequest } = useApiCallWithAuth(updateSchedule);

  function buildRows(accountList: SocialAccountItem[], scheduleList: SocialPostScheduleItem[]): Record<string, DestinationRowState> {
    const next: Record<string, DestinationRowState> = {};
    for (const account of accountList) {
      const pending = scheduleList.find(s => s.socialAccountGuid === account.socialAccountGuid && s.status === 'Pending');
      next[account.socialAccountGuid] = pending
        ? {
          checked: true,
          scheduledFor: toLocalInput(pending.scheduledFor),
          messageOverride: pending.messageOverride ?? '',
          existingGuid: pending.socialPostScheduleGuid,
        }
        : { checked: false, scheduledFor: defaultLocalInput(), messageOverride: '', existingGuid: null };
    }
    return next;
  }

  function load() {
    setLoading(true);
    Promise.all([listAccountsRequest(), listSchedulesRequest(blogId)])
      .then(([accountList, scheduleList]: [SocialAccountItem[], SocialPostScheduleItem[]]) => {
        const active = accountList.filter(a => a.isActive);
        setAccounts(active);
        setSchedules(scheduleList);
        setRows(buildRows(active, scheduleList));
      })
      .catch(() => setError('Failed to load social destinations'))
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, [blogId]); // eslint-disable-line react-hooks/exhaustive-deps

  function setRow(guid: string, patch: Partial<DestinationRowState>) {
    setRows(r => ({ ...r, [guid]: { ...r[guid], ...patch } as DestinationRowState }));
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      const toCreate: { socialAccountGuid: string; scheduledFor: string; messageOverride?: string }[] = [];

      for (const account of accounts) {
        const row = rows[account.socialAccountGuid];
        if (!row) continue;

        if (row.checked) {
          const scheduledForIso = new Date(row.scheduledFor).toISOString();
          const messageOverride = row.messageOverride.trim() || undefined;

          if (row.existingGuid) {
            await updateRequest(row.existingGuid, { scheduledFor: scheduledForIso, messageOverride });
          } else {
            toCreate.push({ socialAccountGuid: account.socialAccountGuid, scheduledFor: scheduledForIso, messageOverride });
          }
        } else if (row.existingGuid) {
          await updateRequest(row.existingGuid, { status: 'Canceled' });
        }
      }

      if (toCreate.length > 0) {
        await upsertRequest(blogId, toCreate);
      }

      load();
    } catch {
      setError('Failed to save schedule');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <CircularProgress size={24} />;

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant='h6' sx={{ mb: 1 }}>Publish to Social</Typography>

      {accounts.length === 0 ? (
        <Typography color='text.secondary' sx={{ mb: 2 }}>
          No connected social accounts. Connect LinkedIn or Facebook from the Social Accounts tab in Articles Admin.
        </Typography>
      ) : (
        <Stack spacing={1.5} sx={{ mb: 2 }}>
          {accounts.map(account => {
            const row = rows[account.socialAccountGuid];
            if (!row) return null;
            return (
              <Stack key={account.socialAccountGuid} direction='row' spacing={2} alignItems='center' flexWrap='wrap'>
                <FormControlLabel
                  sx={{ minWidth: 240 }}
                  control={
                    <Checkbox
                      checked={row.checked}
                      onChange={e => setRow(account.socialAccountGuid, { checked: e.target.checked })}
                    />
                  }
                  label={account.displayName}
                />
                <TextField
                  type='datetime-local'
                  size='small'
                  disabled={!row.checked}
                  value={row.scheduledFor}
                  onChange={e => setRow(account.socialAccountGuid, { scheduledFor: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  size='small'
                  disabled={!row.checked}
                  placeholder='Custom caption (optional)'
                  value={row.messageOverride}
                  onChange={e => setRow(account.socialAccountGuid, { messageOverride: e.target.value })}
                  sx={{ flexGrow: 1, minWidth: 200 }}
                />
              </Stack>
            );
          })}
        </Stack>
      )}

      {error && <Typography color='error' sx={{ mb: 2 }}>{error}</Typography>}

      {accounts.length > 0 && (
        <Button variant='contained' onClick={handleSave} disabled={saving} sx={{ mb: 3 }}>
          {saving ? <CircularProgress size={20} /> : 'Save Schedule'}
        </Button>
      )}

      {schedules.length > 0 && (
        <>
          <Typography variant='subtitle2' sx={{ mb: 1 }}>Schedule History</Typography>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell><strong>Destination</strong></TableCell>
                <TableCell><strong>Scheduled For</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Details</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {schedules.map(schedule => (
                <TableRow key={schedule.socialPostScheduleGuid}>
                  <TableCell>{schedule.displayName}</TableCell>
                  <TableCell>{new Date(schedule.scheduledFor).toLocaleString()}</TableCell>
                  <TableCell>
                    <Chip label={schedule.status} size='small' color={STATUS_COLOR[schedule.status] ?? 'default'} />
                  </TableCell>
                  <TableCell>
                    {schedule.status === 'Posted' && schedule.externalPostUrl && (
                      <Link href={schedule.externalPostUrl} target='_blank' rel='noopener noreferrer'>View post</Link>
                    )}
                    {schedule.status === 'Failed' && schedule.lastError && (
                      <Typography variant='caption' color='error'>{schedule.lastError}</Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
    </Box>
  );
}
